/**
 * Aba "Qualidade do ar" da barra lateral.
 *
 * Responde, num relance, "como está o ar agora onde há medição?" — contando
 * os pontos por faixa no quadro exibido, na escala de cada rede (cada uma
 * aparece como ela mesma publica) — e deixa claro, antes de qualquer cor na
 * grade, que o modelo CAMS é estimativa.
 */
import { AirVent, CircleQuestionMark, ExternalLink, RefreshCw, Settings2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { formatInt } from '../lib/format';
import { formatDateTimeBoth, formatRelative, formatTimeBrt, HOUR_MS } from '../lib/time';
import { shownTimeOf, useTimeStore } from '../state/time';
import { useNow } from '../ui/hooks';
import { Badge, Button, Dialog, Hint, IconButton, Section, Slider, Toggle } from '../ui/primitives';
import {
  AIRGRADIENT_URL,
  BASE_AR,
  CAMS_ATRIBUICAO,
  CAMS_URL,
  FONTES_SENSOR,
  MONITORAR_URL,
  NOME_REDE,
  OPEN_METEO_URL,
  OPENAQ_URL,
  PURPLEAIR_ATRIBUICAO,
  PURPLEAIR_URL,
  REDEAR_URL,
  UFAC_URL,
} from './api';
import {
  ESCALAS,
  ORDEM_ESCALAS,
  corDaFaixa,
  faixasDa,
  intervaloDaFaixa,
  nomeDaFaixa,
  type EscalaId,
  type Faixa,
} from './faixas';
import { faixaNoQuadro } from './layer';
import { escalaDoSensor } from './sensor';
import { Simbolo } from './Simbolo';
import { LIMITES, useArStore } from './store';
import { carregarAr, carregarModelo } from './sync';
import { avisoDeFrescor, indiceQueContem, parado } from './tempo';
import type { FonteSensor } from './types';

const NOME_FONTE: Readonly<Record<string, string>> = {
  ufac: 'Rede do Acre (UFAC/MPAC)',
  redear: 'RedeAr (IPAM/UFPA)',
  airgradient: 'AirGradient',
  purpleair: 'PurpleAir (com chave)',
  openaq: 'OpenAQ (com chave)',
  monitorar: 'MonitorAr (MMA)',
  cams: 'Modelo CAMS (Open-Meteo)',
};

function Link({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-rio-300 hover:underline">
      {children} <ExternalLink size={10} />
    </a>
  );
}

interface ContagemRede {
  rede: FonteSensor | 'monitorar';
  vivos: number;
  noQuadro: number;
}

/** Quantos pontos de cada rede: com dado nas últimas 48 h e com valor no quadro exibido. */
function PorRede({ linhas }: { linhas: readonly ContagemRede[] }) {
  if (!linhas.length) return null;
  return (
    <table className="w-full text-[11px] text-ink-300" aria-label="Pontos por rede">
      <thead>
        <tr className="text-[10px] text-ink-400">
          <th className="pb-0.5 text-left font-normal">Rede</th>
          <th className="pb-0.5 text-right font-normal" title="Com alguma leitura nas últimas 48 h">em 48 h</th>
          <th className="pb-0.5 text-right font-normal" title="Com valor no quadro exibido">agora</th>
        </tr>
      </thead>
      <tbody>
        {linhas.map((l) => (
          <tr key={l.rede}>
            <td className="py-0.5">
              <span className="inline-flex items-center gap-1.5">
                {l.rede === 'monitorar' ? (
                  <Simbolo forma="losango" cor={corDaFaixa('iqar:0')} anel tamanho={9} />
                ) : (
                  <Simbolo forma="hexagono" cor={corDaFaixa(`${escalaDoSensor(l.rede)}:0`)} tamanho={9} />
                )}
                {NOME_REDE[l.rede]}
              </span>
            </td>
            <td className="text-right tabular-nums">{formatInt(l.vivos)}</td>
            <td className="text-right text-ink-200 tabular-nums">{formatInt(l.noQuadro)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Para que serve cada escala na legenda. */
const USO_ESCALA: Readonly<Record<EscalaId, string>> = {
  ufac: 'sensores da rede do Acre · MP2,5 µg/m³ corrigido pela rede',
  redear: 'sensores da RedeAr · MP2,5 µg/m³, média dos canais A e B',
  purpleair: 'sensores consultados na PurpleAir · AQI da EPA sobre a média de 10 min',
  airgradient: 'sensores da AirGradient · MP2,5 µg/m³ do mapa dela',
  iqar: 'estações oficiais · IQAr do MonitorAr',
  conama: 'OpenAQ e modelo CAMS (sem escala própria) · MP2,5 µg/m³',
};

/** Uma escala: as faixas dela, com o intervalo na unidade dela e quantos pontos estão em cada uma agora. */
function LegendaEscala({ escala, contagem }: { escala: EscalaId; contagem: Map<Faixa, number> }) {
  const e = ESCALAS[escala];
  return (
    <table className="w-full text-[11px] text-ink-300">
      <caption className="pb-0.5 text-left text-[11px] text-ink-200">
        {e.nome}
        <span className="block text-[10px] text-ink-400">{USO_ESCALA[escala]}</span>
      </caption>
      <thead className="sr-only">
        <tr>
          <th>Faixa</th>
          <th>{e.unidade}</th>
          <th>agora</th>
        </tr>
      </thead>
      <tbody>
        {faixasDa(escala).map((f) => (
          <tr key={f}>
            <td className="py-0.5">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block size-2.5 rounded-[2px]" style={{ background: corDaFaixa(f) }} aria-hidden />
                {nomeDaFaixa(f)}
              </span>
            </td>
            <td className="text-right tabular-nums">
              {intervaloDaFaixa(f)} <span className="text-ink-500">{e.unidade}</span>
            </td>
            <td className="w-8 text-right text-ink-200 tabular-nums" title="Pontos nesta faixa no quadro exibido">
              {contagem.get(f) ?? 0}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Legenda({ contagem, escalas }: { contagem: Map<Faixa, number>; escalas: readonly EscalaId[] }) {
  const exemplo = escalas.find((e) => e !== 'iqar' && e !== 'conama') ?? escalas[0] ?? 'redear';
  return (
    <div className="space-y-2.5">
      {escalas.map((e) => (
        <LegendaEscala key={e} escala={e} contagem={contagem} />
      ))}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-ink-300">
        <span className="inline-flex items-center gap-1">
          <Simbolo forma="hexagono" cor={corDaFaixa(`${exemplo}:0`)} /> sensor (baixo custo)
        </span>
        <span className="inline-flex items-center gap-1">
          <Simbolo forma="losango" cor={corDaFaixa('iqar:0')} anel /> estação oficial
        </span>
        <span className="inline-flex items-center gap-1">
          <Simbolo forma="hexagono" cor={null} /> sem dado neste instante
        </span>
      </div>
    </div>
  );
}

function AparenciaDialog(props: { open: boolean; onClose: () => void }) {
  const estilo = useArStore((s) => s.estilo);
  const setEstilo = useArStore((s) => s.setEstilo);
  return (
    <Dialog title="Aparência da qualidade do ar" open={props.open} onClose={props.onClose}>
      <div className="space-y-4 p-4">
        <Slider label="Tamanho" value={estilo.tamanho} {...LIMITES.tamanho} onChange={(tamanho) => setEstilo({ tamanho })} />
        <Slider
          label="Opacidade dos pontos"
          value={estilo.opacidade}
          {...LIMITES.opacidade}
          onChange={(opacidade) => setEstilo({ opacidade })}
          format={(v) => `${Math.round(v * 100)}%`}
        />
        <Toggle
          label="Mostrar sensores e estações parados"
          checked={estilo.mostrarParados}
          onChange={(mostrarParados) => setEstilo({ mostrarParados })}
          description="Pequenos e vazados: sem nenhuma leitura nas últimas 48 h."
        />
      </div>
    </Dialog>
  );
}

export function ArPanel() {
  const visivel = useArStore((s) => s.visivel);
  const estilo = useArStore((s) => s.estilo);
  const situacao = useArStore((s) => s.situacao);
  const situacaoModelo = useArStore((s) => s.situacaoModelo);
  const pontos = useArStore((s) => s.pontos);
  const cams = useArStore((s) => s.cams);
  const { setVisivel, setEstilo } = useArStore.getState();
  const instante = useTimeStore((s) => shownTimeOf(s));
  const agora = useNow(60_000);
  const [aparenciaAberta, setAparenciaAberta] = useState(false);

  const aviso = avisoDeFrescor(pontos.gerado, agora);

  const { contagem, comDado, semDado, parados, porRede, escalas } = useMemo(() => {
    const m = new Map<Faixa, number>();
    const presentes = new Set<EscalaId>();
    const redes = new Map<FonteSensor | 'monitorar', ContagemRede>();
    let comDado = 0;
    let semDado = 0;
    let parados = 0;
    for (const p of [...pontos.estacoes, ...pontos.sensores]) {
      if (parado(p.tipo === 'sensor' ? p.ultima.t : p.ultima, agora)) {
        parados++;
        continue;
      }
      const rede = p.tipo === 'sensor' ? p.fonte : 'monitorar';
      presentes.add(p.tipo === 'sensor' ? escalaDoSensor(p.fonte) : 'iqar');
      const r = redes.get(rede) ?? { rede, vivos: 0, noQuadro: 0 };
      r.vivos++;
      redes.set(rede, r);
      const f = faixaNoQuadro(p, pontos, instante);
      if (f) {
        m.set(f, (m.get(f) ?? 0) + 1);
        r.noQuadro++;
        comDado++;
      } else semDado++;
    }
    const ordem: (FonteSensor | 'monitorar')[] = [...FONTES_SENSOR, 'monitorar'];
    const porRede = ordem.map((k) => redes.get(k)).filter((r): r is ContagemRede => !!r);
    if (estilo.modelo) presentes.add('conama');
    const escalas = ORDEM_ESCALAS.filter((e) => presentes.has(e));
    return { contagem: m, comDado, semDado, parados, porRede, escalas };
  }, [pontos, instante, agora, estilo.modelo]);

  const vivosSensores = porRede.filter((r) => r.rede !== 'monitorar').reduce((n, r) => n + r.vivos, 0);
  const vivasEstacoes = porRede.find((r) => r.rede === 'monitorar')?.vivos ?? 0;
  const temPurpleAir = pontos.sensores.some((x) => x.pa && !parado(x.ultima.t, agora));
  const horaModelo = cams ? indiceQueContem(cams.horas, cams.h0, HOUR_MS, instante) : -1;
  // A grade publicada pode parar antes da hora exibida (o espelho não conseguiu
  // baixar a rodada nova): aí o mapa fica sem a grade, e o painel diz até quando há.
  const ultimaHoraModelo = useMemo(() => {
    if (!cams) return -1;
    for (let h = cams.horas - 1; h >= 0; h--) if (cams.pm25.some((s) => typeof s[h] === 'number')) return h;
    return -1;
  }, [cams]);
  const modeloSemValor = horaModelo >= 0 && !cams!.pm25.some((s) => typeof s[horaModelo] === 'number');

  if (!BASE_AR) {
    return (
      <Section title="Qualidade do ar" icon={AirVent}>
        <Hint>A camada de qualidade do ar está desligada nesta versão.</Hint>
      </Section>
    );
  }

  return (
    <div>
      <Section
        title="Qualidade do ar"
        icon={AirVent}
        right={
          visivel && (
            <IconButton
              icon={Settings2}
              size="sm"
              label="Aparência da qualidade do ar"
              onClick={() => setAparenciaAberta(true)}
              className="pointer-coarse:size-11"
            />
          )
        }
      >
        <Toggle
          label="MP2,5 medido (sensores e estações)"
          checked={visivel}
          onChange={setVisivel}
          description="Partículas finas da fumaça e da poluição, medidas no chão, animadas junto com o satélite."
        />

        {visivel && aviso && (
          <div className="rounded-lg border border-alerta-400/40 bg-alerta-400/10 px-2.5 py-1.5 text-[11px] leading-snug text-alerta-200">
            {aviso}
          </div>
        )}
        {visivel && situacao.estado === 'error' && pontos.gerado === null && (
          <div className="rounded-lg border border-alerta-400/40 bg-alerta-400/10 px-2.5 py-1.5 text-[11px] leading-snug text-alerta-200">
            Não deu para baixar as medições: {situacao.erro}
          </div>
        )}

        {visivel && pontos.gerado !== null && (
          <>
            {/* A contagem por faixa fica em cada legenda (as escalas têm nomes repetidos). */}
            <div className="flex flex-wrap items-center gap-1.5">
              {comDado > 0 && <Badge>{formatInt(comDado)} com dado neste instante</Badge>}
              {semDado > 0 && <Badge>{formatInt(semDado)} sem dado</Badge>}
              {comDado === 0 && semDado === 0 && <Badge>nenhum ponto com medição nas últimas 48 h</Badge>}
            </div>
            <Legenda contagem={contagem} escalas={escalas} />
            <PorRede linhas={porRede} />
            <Hint>
              {formatInt(vivosSensores)} sensores e {formatInt(vivasEstacoes)}{' '}
              {vivasEstacoes === 1 ? 'estação oficial' : 'estações oficiais'} com dado em 48 h
              {parados > 0 && <> ({formatInt(parados)} parados)</>}. Cada rede no próprio valor e na própria escala.
              {temPurpleAir && <> {PURPLEAIR_ATRIBUICAO}.</>}
            </Hint>
          </>
        )}

        {visivel && (
          <div className="space-y-2 rounded-lg border border-ink-700/70 bg-ink-800/40 p-2.5">
            <Toggle
              label="Complemento: modelo CAMS (estimativa, não medição)"
              checked={estilo.modelo}
              onChange={(modelo) => setEstilo({ modelo })}
              size="sm"
              description="MP2,5 do modelo global do Copernicus em células de 0,8°, onde não há medição. Pode divergir muito do que os sensores medem."
            />
            {estilo.modelo && (
              <>
                <div className="flex items-center gap-2 text-[11px] text-ink-300">
                  <span className="flex gap-px" aria-hidden>
                    {faixasDa('conama').map((f) => (
                      <span key={f} className="inline-block h-2.5 w-3 opacity-60" style={{ background: corDaFaixa(f) }} />
                    ))}
                  </span>
                  MP2,5 do modelo pela tabela do CONAMA 491, translúcida
                </div>
                {situacaoModelo.estado === 'loading' && <div className="text-[11px] text-ink-400">Baixando a grade do modelo…</div>}
                {situacaoModelo.estado === 'error' && !cams && (
                  <div className="text-[11px] text-alerta-300">A grade do modelo não carregou: {situacaoModelo.erro}</div>
                )}
                {cams && (
                  <div className="text-[11px] leading-snug text-ink-400">
                    {modeloSemValor ? (
                      <span className="text-alerta-300">
                        O modelo não tem valor para este horário: a grade publicada vai até{' '}
                        {ultimaHoraModelo >= 0 ? formatDateTimeBoth(cams.h0 + ultimaHoraModelo * HOUR_MS) : '—'}.
                      </span>
                    ) : horaModelo >= 0 ? (
                      <>Hora do modelo no mapa: {formatTimeBrt(cams.h0 + horaModelo * HOUR_MS)} (Brasília).</>
                    ) : (
                      <>O quadro exibido está fora das 48 h do modelo: a grade some.</>
                    )}
                    {cams.run !== null && <> Rodada de {formatDateTimeBoth(cams.run)}.</>}
                  </div>
                )}
                <Slider
                  label="Opacidade do modelo"
                  value={estilo.opacidadeModelo}
                  {...LIMITES.opacidadeModelo}
                  onChange={(opacidadeModelo) => setEstilo({ opacidadeModelo })}
                  format={(v) => `${Math.round(v * 100)}%`}
                />
              </>
            )}
          </div>
        )}

        {visivel && <Hint>Toque num ponto ou numa célula do modelo para ver a série de 48 h.</Hint>}
      </Section>

      <Section title="Dados" icon={RefreshCw} defaultOpen={false} storageKey="ar-dados">
        <div className="space-y-1.5 text-xs text-ink-300">
          {situacao.estado === 'loading' && <div>Baixando…</div>}
          {situacao.estado === 'error' && <div className="text-alerta-300">Falha ao atualizar: {situacao.erro}</div>}
          {pontos.gerado !== null && (
            <div>
              {formatInt(pontos.sensores.length)} sensores e {formatInt(pontos.estacoes.length)} estações · espelho atualizado{' '}
              {formatRelative(pontos.gerado, agora)}
            </div>
          )}
          {Object.entries(pontos.fontes).map(([nome, f]) =>
            f.nota === 'sem chave' ? (
              <div key={nome} className="text-ink-400">
                {NOME_FONTE[nome] ?? nome}: inativa (o espelho não tem chave)
              </div>
            ) : (
              <div key={nome} className={f.erro ? 'text-alerta-300' : undefined}>
                {NOME_FONTE[nome] ?? nome}: {f.em !== null ? `respondeu ${formatRelative(f.em, agora)}` : 'ainda não respondeu'}
                {f.nota && <> · {f.nota}</>}
                {f.saldo !== null && <> · saldo {formatInt(f.saldo)} pontos</>}
                {f.erro && <> · última tentativa falhou ({f.erro})</>}
              </div>
            ),
          )}
          <Button
            icon={RefreshCw}
            onClick={() => {
              void carregarAr(true);
              if (estilo.modelo) void carregarModelo(true);
            }}
          >
            Atualizar agora
          </Button>
        </div>
      </Section>

      <Section title="O que é" icon={CircleQuestionMark} defaultOpen={false} storageKey="ar-oque">
        <div className="space-y-2 text-xs leading-relaxed text-ink-300">
          <p>
            O <strong>MP2,5</strong> são partículas com menos de 2,5 milésimos de milímetro — a parte da fumaça das queimadas
            que entra fundo no pulmão. É o poluente que mais pesa na saúde durante a temporada de fogo.
          </p>
          <p>
            <strong>Sensores (hexágonos):</strong> aparelhos de baixo custo (PurpleAir, AirGradient e os da própria RedeAr)
            instalados por órgãos e instituições da região: a Rede de Qualidade do Ar do Acre (UFAC com o Ministério Público
            do Acre), em todos os municípios do estado; a RedeAr (IPAM/UFPA), que reúne os das secretarias de meio ambiente
            (SEMA-AM, SEMAS-PA, SEMA-MT), das universidades (UEA, UFMT, UNEMAT, IFMT…) e do IPAM; e os pontos públicos da
            AirGradient. Medem a cada ~2 min; o AmaView guarda as leituras de 5 em 5 min por 48 h.
          </p>
          <p>
            <strong>Cada rede com a sua régua:</strong> o AmaView mostra o dado como cada rede publica, sem corrigir nem
            reclassificar. A rede do Acre corrige o sensor pela fórmula da LRAPA e usa faixas próprias; a RedeAr mostra a
            média dos dois canais do sensor, sem correção, com faixas próprias (boa até 15 µg/m³); a PurpleAir mostra o AQI
            da EPA dos EUA sobre a média de 10 min; a AirGradient, o valor corrigido por ela nas faixas da EPA. Por isso o
            mesmo ar pode aparecer com números e cores diferentes em redes vizinhas — e a ficha de cada ponto diz de quem é
            o número e a escala. Um sensor que aparece em duas redes vira um ponto só, com o valor de uma delas e o da outra
            na ficha.
          </p>
          <p>
            <strong>Estações oficiais (losangos):</strong> o MonitorAr, do Ministério do Meio Ambiente, reúne as estações
            dos órgãos estaduais e publica o IQAr com a classificação da Resolução CONAMA 491/2018. Na Amazônia Legal só as
            de São Luís têm mandado dado recente; as do Pará aparecem paradas.
          </p>
          <p>
            <strong>Modelo CAMS (grade):</strong> estimativa do serviço Copernicus de monitoramento da atmosfera (ECMWF),
            com células de ~40 km, rodado duas vezes por dia. Não é medição: serve para ver a tendência regional onde não
            há sensor, e erra — às vezes muito — perto da fumaça local.
          </p>
          <p>
            A tabela oficial brasileira (IQAr, CONAMA 491) considera boa, para o MP2,5, até 25 µg/m³ em 24 h; ela vale para
            as estações e colore a OpenAQ e o modelo, que não têm escala própria. A Organização Mundial da Saúde recomenda não
            passar de 15 µg/m³ na média de 24 h.
          </p>
          <p className="text-ink-400">
            Fontes: <Link href={UFAC_URL}>Rede de Qualidade do Ar do Acre</Link> · <Link href={REDEAR_URL}>RedeAr</Link> ·{' '}
            <Link href={AIRGRADIENT_URL}>AirGradient</Link>
            {pontos.sensores.some((x) => x.fonte === 'openaq') && (
              <>
                {' '}
                · <Link href={OPENAQ_URL}>OpenAQ</Link>
              </>
            )}{' '}
            · <Link href={MONITORAR_URL}>MonitorAr/MMA</Link> · <Link href={CAMS_URL}>CAMS/Copernicus</Link> via{' '}
            <Link href={OPEN_METEO_URL}>Open-Meteo</Link>. <Link href={PURPLEAIR_URL}>{PURPLEAIR_ATRIBUICAO}</Link>.{' '}
            {CAMS_ATRIBUICAO}.
          </p>
          <p className="text-ink-400">Não é alerta oficial de saúde. Em caso de fumaça, siga as orientações da Defesa Civil e da Saúde.</p>
        </div>
      </Section>

      <AparenciaDialog open={aparenciaAberta} onClose={() => setAparenciaAberta(false)} />
    </div>
  );
}
